import { Fragment, useState } from 'react';
import { format, addDays, parseISO } from 'date-fns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import { ClockIcon, CalendarIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  getPortalAppointmentTypes,
  getPortalAvailability,
  bookPortalAppointment,
} from '../../services/portalService';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import EmptyState from '../../components/common/EmptyState';

export default function PortalSchedule() {
  const queryClient = useQueryClient();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [step, setStep] = useState(1);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [selectedDate, setSelectedDate] = useState(today);
  const [confirmingSlot, setConfirmingSlot] = useState(null); // ISO slot string

  const { data: typesData, isLoading: typesLoading } = useQuery({
    queryKey: ['portal', 'appointmentTypes'],
    queryFn: getPortalAppointmentTypes,
  });

  const { data: avail, isLoading: availLoading } = useQuery({
    queryKey: ['portal', 'availability', selectedDate, selectedTypeId],
    queryFn: () => getPortalAvailability(selectedDate, selectedTypeId || undefined),
    enabled: !!selectedDate && step === 3,
  });

  const bookMutation = useMutation({
    mutationFn: (startTime) =>
      bookPortalAppointment({ startTime, appointmentTypeId: selectedTypeId || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'appointments'] });
      queryClient.invalidateQueries({ queryKey: ['portal', 'availability'] });
      setConfirmingSlot(null);
      toast.success('Request submitted — your provider will confirm it shortly.');
      // Reset to beginning
      setStep(1);
      setSelectedTypeId('');
      setSelectedDate(today);
    },
    onError: (err) => {
      setConfirmingSlot(null);
      toast.error(err.response?.data?.message || 'Failed to book appointment');
    },
  });

  const slots = avail?.slots ?? [];
  const durationMinutes = avail?.durationMinutes ?? 50;
  const types = typesData ?? [];

  // Build the next 30 days for the date strip
  const dateOptions = Array.from({ length: 30 }, (_, i) => {
    const d = addDays(new Date(), i);
    return {
      value: format(d, 'yyyy-MM-dd'),
      dayLabel: format(d, 'EEE'),
      dayNum: format(d, 'd'),
      monthLabel: format(d, 'MMM'),
    };
  });

  const selectedType = types.find((t) => t.id === selectedTypeId);
  const confirmingSlotDate = confirmingSlot ? new Date(confirmingSlot) : null;

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <h1>Schedule an appointment</h1>

      {/* Step indicators */}
      <div className="flex items-center gap-2">
        {['Type', 'Date', 'Time'].map((label, i) => {
          const n = i + 1;
          const active = step === n;
          const done = step > n;
          return (
            <Fragment key={label}>
              <button
                onClick={() => done ? setStep(n) : undefined}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  active ? 'bg-teal-600 text-white' :
                  done ? 'text-teal-600 dark:text-teal-400 cursor-pointer hover:bg-teal-50 dark:hover:bg-teal-900/20' :
                  'text-neutral-400 dark:text-neutral-500'
                }`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                  active ? 'bg-white/20' : done ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400' : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-500'
                }`}>{n}</span>
                {label}
              </button>
              {i < 2 && <div className="h-px flex-1 bg-neutral-200 dark:bg-neutral-700" />}
            </Fragment>
          );
        })}
      </div>

      {/* Step 1 — Choose type */}
      {step === 1 && (
        <div className="space-y-3">
          <h2 className="text-base text-neutral-600 dark:text-neutral-400">What type of session?</h2>
          {typesLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => <SkeletonBlock key={i} className="h-20" rounded="rounded-xl" />)}
            </div>
          ) : types.length === 0 ? (
            <EmptyState
              icon={CalendarIcon}
              compact
              title="No appointment types available"
              description="Contact your provider to set up scheduling."
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {types.map((t) => (
                <button
                  key={t.id}
                  onClick={() => {
                    setSelectedTypeId(t.id);
                    setStep(2);
                  }}
                  className={`card-hover flex items-start gap-3 text-left ${
                    selectedTypeId === t.id ? 'ring-2 ring-teal-500' : ''
                  }`}
                >
                  <div className="w-10 h-10 rounded-xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center shrink-0">
                    <ClockIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900 dark:text-neutral-100">{t.name}</p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">{t.durationMinutes} minutes</p>
                  </div>
                </button>
              ))}
            </div>
          )}
          {types.length > 0 && (
            <button onClick={() => { setSelectedTypeId(''); setStep(2); }} className="text-sm text-teal-600 dark:text-teal-400 hover:underline">
              Skip — any type
            </button>
          )}
        </div>
      )}

      {/* Step 2 — Choose date */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base text-neutral-600 dark:text-neutral-400">
              {selectedType ? `${selectedType.name} — ` : ''}Choose a date
            </h2>
            <button onClick={() => setStep(1)} className="text-sm text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300">
              ← Back
            </button>
          </div>

          {/* Horizontal scrollable date strip */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1">
            {dateOptions.map(({ value, dayLabel, dayNum, monthLabel }) => {
              const isSelected = selectedDate === value;
              return (
                <button
                  key={value}
                  onClick={() => {
                    setSelectedDate(value);
                    setStep(3);
                  }}
                  className={`shrink-0 flex flex-col items-center px-3 py-2.5 rounded-xl border text-sm transition-colors min-w-[58px] ${
                    isSelected
                      ? 'bg-teal-600 border-teal-600 text-white'
                      : 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:border-teal-400 dark:hover:border-teal-600'
                  }`}
                >
                  <span className={`text-[10px] font-medium ${isSelected ? 'text-teal-100' : 'text-neutral-400 dark:text-neutral-500'}`}>
                    {dayLabel}
                  </span>
                  <span className="text-base font-bold leading-tight">{dayNum}</span>
                  <span className={`text-[10px] ${isSelected ? 'text-teal-100' : 'text-neutral-400 dark:text-neutral-500'}`}>
                    {monthLabel}
                  </span>
                </button>
              );
            })}
          </div>

          <p className="text-xs text-neutral-400 dark:text-neutral-500">
            Tap a date to see available times
          </p>
        </div>
      )}

      {/* Step 3 — Choose time */}
      {step === 3 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base text-neutral-600 dark:text-neutral-400">
              {format(parseISO(selectedDate), 'EEEE, MMMM d')}
            </h2>
            <button onClick={() => setStep(2)} className="text-sm text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300">
              ← Change date
            </button>
          </div>

          {availLoading ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {[...Array(8)].map((_, i) => (
                <SkeletonBlock key={i} className="h-10" rounded="rounded-lg" />
              ))}
            </div>
          ) : slots.length === 0 ? (
            <EmptyState
              icon={CalendarIcon}
              compact
              title="No available slots"
              description="No openings on this day. Try a different date."
              action={<button onClick={() => setStep(2)} className="btn btn-secondary btn-sm">Choose another date</button>}
            />
          ) : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {slots.map((slot) => (
                  <button
                    key={slot}
                    onClick={() => setConfirmingSlot(slot)}
                    className="px-3 py-2.5 rounded-xl text-sm font-medium border transition-colors
                               bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300
                               border-neutral-200 dark:border-neutral-700
                               hover:border-teal-500 hover:text-teal-600 dark:hover:border-teal-500 dark:hover:text-teal-400"
                  >
                    {format(new Date(slot), 'h:mm a')}
                  </button>
                ))}
              </div>
              {durationMinutes && (
                <p className="text-xs text-neutral-400 dark:text-neutral-500">
                  Session length: {durationMinutes} minutes
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Confirmation dialog */}
      <Transition show={!!confirmingSlot} as={Fragment}>
        <Dialog onClose={() => setConfirmingSlot(null)} className="relative z-50">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-neutral-950/50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-sm bg-white dark:bg-neutral-800 rounded-2xl shadow-modal p-6">
                <Dialog.Title className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
                  Confirm appointment
                </Dialog.Title>

                <div className="bg-teal-50 dark:bg-teal-900/20 rounded-xl p-4 mb-5 space-y-1">
                  {selectedType && (
                    <p className="text-sm font-medium text-teal-900 dark:text-teal-100">
                      {selectedType.name}
                    </p>
                  )}
                  {confirmingSlotDate && (
                    <>
                      <p className="text-sm text-teal-700 dark:text-teal-300">
                        {format(confirmingSlotDate, 'EEEE, MMMM d, yyyy')}
                      </p>
                      <p className="text-sm text-teal-700 dark:text-teal-300">
                        {format(confirmingSlotDate, 'h:mm a')}
                        {durationMinutes && ` · ${durationMinutes} min`}
                      </p>
                    </>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setConfirmingSlot(null)}
                    className="btn btn-secondary flex-1"
                    disabled={bookMutation.isPending}
                  >
                    Choose different time
                  </button>
                  <button
                    onClick={() => bookMutation.mutate(confirmingSlot)}
                    className="btn btn-primary flex-1"
                    disabled={bookMutation.isPending}
                  >
                    {bookMutation.isPending ? 'Booking…' : 'Book appointment'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
